import React, { useState, useEffect } from 'react';
import { Bug, CheckCircle, XCircle, Clock, Loader2, AlertTriangle } from 'lucide-react';
import {
  isNativeAlarmAvailable,
  getNativeAlarmStatus,
  scheduleNativeDivineReminder,
} from '../services/nativeAlarmBridge';

interface TestResult {
  step: string;
  status: 'pass' | 'fail' | 'warn';
  detail: string;
}

export const NativeAlarmDebug: React.FC = () => {
  const [showDebug, setShowDebug] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [testing, setTesting] = useState(false);
  const [platformInfo, setPlatformInfo] = useState('');

  // Auto-detect platform info on mount
  useEffect(() => {
    const info: string[] = [];
    try {
      info.push('NativeAlarm: ' + (typeof (window as any).NativeAlarm !== 'undefined' ? 'YES' : 'NO'));
      info.push('androidBridge: ' + (typeof (window as any).androidBridge !== 'undefined' ? 'YES' : 'NO'));
      try {
        const { Capacitor } = require('@capacitor/core');
        info.push('Capacitor.getPlatform: ' + Capacitor.getPlatform());
        info.push('isNativePlatform: ' + Capacitor.isNativePlatform());
      } catch {
        info.push('Capacitor: NOT LOADED');
      }
      info.push('userAgent: ' + (navigator.userAgent.includes('Android') ? 'Android' : 'Web'));
    } catch (e: any) {
      info.push('Error: ' + e.message);
    }
    setPlatformInfo(info.join(' | '));
  }, []);

  const addResult = (r: TestResult) => setResults(prev => [...prev, r]);

  const runDiagnostics = async () => {
    setResults([]);
    setTesting(true);

    // Test 0: Platform detection
    const hasNativeAlarm = typeof (window as any).NativeAlarm !== 'undefined';
    const hasAndroidBridge = typeof (window as any).androidBridge !== 'undefined';
    let capacitorPlatform = 'unknown';
    let capacitorNative = false;
    try {
      const { Capacitor } = require('@capacitor/core');
      capacitorPlatform = Capacitor.getPlatform();
      capacitorNative = Capacitor.isNativePlatform();
    } catch {}

    addResult({
      step: '0. Platform Detection',
      status: (hasNativeAlarm || hasAndroidBridge || capacitorNative) ? 'pass' : 'fail',
      detail: `NativeAlarm=${hasNativeAlarm} | androidBridge=${hasAndroidBridge} | Capacitor.platform=${capacitorPlatform} | isNative=${capacitorNative}`,
    });

    // Test 1: Check if window.NativeAlarm exists
    const bridgeAvailable = isNativeAlarmAvailable();
    addResult({
      step: '1. Native Bridge (window.NativeAlarm)',
      status: bridgeAvailable ? 'pass' : 'fail',
      detail: bridgeAvailable
        ? 'window.NativeAlarm FOUND — Java bridge connected!'
        : 'window.NativeAlarm NOT FOUND — NativeAlarmBridge.java missing ya MainActivity me register nahi hua',
    });

    if (!bridgeAvailable) {
      addResult({
        step: '1b. DIAGNOSIS',
        status: 'fail',
        detail: 'MainActivity.java me check karo: webView.addJavascriptInterface(new NativeAlarmBridge(this, webView), "NativeAlarm") hai ya nahi? Aur package name .plugins. (chhota p) hai?',
      });
      setTesting(false);
      return;
    }

    // Test 2: Call getStatus (simple read — tests bridge communication)
    try {
      const status = getNativeAlarmStatus('divine_reminder');
      addResult({
        step: '2. Bridge Communication (getStatus)',
        status: 'pass',
        detail: `Java se response aaya: enabled=${status.enabled}, mode=${status.mode || 'none'}`,
      });
    } catch (err: any) {
      addResult({
        step: '2. Bridge Communication (getStatus)',
        status: 'fail',
        detail: `Error: ${err.message || err}`,
      });
      setTesting(false);
      return;
    }

    // Test 3: Schedule a 1-minute test alarm
    try {
      const result = scheduleNativeDivineReminder(1, 'TEST ALARM — 1 min me aayegi', 'mantra_ram');
      if (result.success && result.alarmsScheduled > 0) {
        addResult({
          step: '3. Schedule Test Alarm (1 min)',
          status: 'pass',
          detail: `${result.alarmsScheduled} alarms scheduled! Ab app KILL karo aur 1 min wait karo.`,
        });
      } else {
        addResult({
          step: '3. Schedule Test Alarm (1 min)',
          status: 'fail',
          detail: `success=${result.success}, alarms=${result.alarmsScheduled}, msg=${result.message}`,
        });
      }
    } catch (err: any) {
      addResult({
        step: '3. Schedule Test Alarm (1 min)',
        status: 'fail',
        detail: `Error: ${err.message || err}`,
      });
    }

    // Test 4: Check notification permission
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const perm = await LocalNotifications.requestPermissions();
      addResult({
        step: '4. Notification Permission',
        status: perm.display === 'granted' ? 'pass' : 'warn',
        detail: perm.display === 'granted'
          ? 'Permission GRANTED'
          : `Permission: ${perm.display}. Settings > Apps > Ram Naam Bank > Notifications > Allow`,
      });
    } catch (err: any) {
      addResult({
        step: '4. Notification Permission',
        status: 'warn',
        detail: `LocalNotifications not available (ok if using native bridge): ${err.message || err}`,
      });
    }

    setTesting(false);
  };

  // ALWAYS show debug button — do NOT gate on isNativeApp
  if (!showDebug) {
    return (
      <div className="space-y-2">
        <button
          onClick={() => setShowDebug(true)}
          className="w-full py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-xs font-bold text-blue-700 dark:text-blue-300 flex items-center justify-center gap-2"
          data-testid="show-debug-btn"
        >
          <Bug size={14} /> Debug: Native Alarm Test
        </button>
        {platformInfo && (
          <div className="text-[10px] text-gray-400 dark:text-gray-600 text-center break-all px-2" data-testid="platform-info">
            {platformInfo}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold text-blue-800 dark:text-blue-300 flex items-center gap-2">
          <Bug size={14} /> Native Alarm Diagnostics
        </h4>
        <button onClick={() => setShowDebug(false)} className="text-xs text-blue-500">Hide</button>
      </div>

      {/* Platform Info Banner */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-2 text-[10px] text-gray-600 dark:text-gray-400 break-all" data-testid="platform-detail">
        {platformInfo}
      </div>

      <button
        onClick={runDiagnostics}
        disabled={testing}
        className="w-full py-2 bg-blue-600 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="run-diagnostics-btn"
      >
        {testing ? (
          <><Loader2 size={14} className="animate-spin" /> Testing...</>
        ) : (
          <><Clock size={14} /> Run Diagnostics + Schedule Test Alarm (1 min)</>
        )}
      </button>

      {results.length > 0 && (
        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className={`flex items-start gap-2 p-2 rounded-lg text-xs ${
              r.status === 'pass'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                : r.status === 'warn'
                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
            }`}>
              {r.status === 'pass'
                ? <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                : r.status === 'warn'
                ? <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                : <XCircle size={14} className="mt-0.5 flex-shrink-0" />}
              <div>
                <p className="font-bold">{r.step}</p>
                <p className="opacity-80 break-all">{r.detail}</p>
              </div>
            </div>
          ))}

          {results.every(r => r.status === 'pass') && (
            <div className="bg-green-200 dark:bg-green-900/50 p-2 rounded-lg text-xs text-green-900 dark:text-green-200 font-bold text-center">
              SAB PASS! Ab app KILL karo aur 1 min wait karo — notification + audio dono aani chahiye!
            </div>
          )}

          {results.some(r => r.status === 'fail') && (
            <div className="bg-red-200 dark:bg-red-900/50 p-2 rounded-lg text-xs text-red-900 dark:text-red-200 font-bold text-center">
              Iska SCREENSHOT bhejo — exact problem identify hogi!
            </div>
          )}
        </div>
      )}
    </div>
  );
};
